import { StorageKeys } from 'common'
import { UserCredentials } from '../../api/user/interfaces'
import { getAwsConfig } from '../../aws-exports'

// Set the user session in localstorage based in the credentials received.
export const setUserSession = (
  data: UserCredentials,
  impersonated: string,
  isSalespad: string
): void => {
  const awsConfig = getAwsConfig()

  const cognitoKeyBase = `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}`

  localStorage.setItem(`${cognitoKeyBase}.LastAuthUser`, data.userId)
  const cognitoUserKeyBase = `${cognitoKeyBase}.${data.userId}`

  localStorage.setItem(
    `${cognitoUserKeyBase}.accessToken`,
    data.tokens.accessToken
  )
  localStorage.setItem(`${cognitoUserKeyBase}.idToken`, data.tokens.idToken)
  localStorage.setItem(
    `${cognitoUserKeyBase}.refreshToken`,
    data.tokens.refreshToken
  )

  localStorage.setItem(
    StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY,
    (impersonated === 'true').toString()
  )

  localStorage.setItem(StorageKeys.AUTO_LOGIN_ORIGIN_KEY, data.domain)

  localStorage.setItem(
    StorageKeys.SALESPAD_KEY,
    (isSalespad === 'true').toString()
  )

  localStorage.setItem(`${cognitoUserKeyBase}.deviceKey`, data.tokens.deviceKey)
}

interface SessionBackup {
  lastAuthUser: string
  accessToken: string
  idToken: string
  refreshToken: string
  deviceKey: string
  impersonated: string
  origin: string
  isSalespad: string
}

// Get the current session from localstorage
export const getCurrentSession = (): SessionBackup | null => {
  const awsConfig = getAwsConfig()
  const cognitoKeyBase = `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}`

  const lastAuthUser = localStorage.getItem(`${cognitoKeyBase}.LastAuthUser`)
  if (!lastAuthUser) return null

  const cognitoUserKeyBase = `${cognitoKeyBase}.${lastAuthUser}`

  return {
    lastAuthUser,
    accessToken:
      localStorage.getItem(`${cognitoUserKeyBase}.accessToken`) || '',
    idToken: localStorage.getItem(`${cognitoUserKeyBase}.idToken`) || '',
    refreshToken:
      localStorage.getItem(`${cognitoUserKeyBase}.refreshToken`) || '',
    deviceKey: localStorage.getItem(`${cognitoUserKeyBase}.deviceKey`) || '',
    impersonated:
      localStorage.getItem(StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY) || 'false',
    origin: localStorage.getItem(StorageKeys.AUTO_LOGIN_ORIGIN_KEY) || '',
    isSalespad: localStorage.getItem(StorageKeys.SALESPAD_KEY) || 'false',
  }
}

export const backupSession = (session: SessionBackup | null): void => {
  if (session) {
    localStorage.setItem(StorageKeys.BACKED_UP_SESSION, JSON.stringify(session))
  }
}

// Backup the current session to localstorage
export const backupCurrentSession = (): void => {
  const currentSession = getCurrentSession()
  backupSession(currentSession)
}

// Restore the backed up session from localstorage
export const restoreBackedUpSession = (): boolean => {
  const backedUpSessionStr = localStorage.getItem(StorageKeys.BACKED_UP_SESSION)
  if (!backedUpSessionStr) return false

  try {
    const backedUpSession: SessionBackup = JSON.parse(backedUpSessionStr)
    const awsConfig = getAwsConfig()
    const cognitoKeyBase = `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}`

    localStorage.setItem(
      `${cognitoKeyBase}.LastAuthUser`,
      backedUpSession.lastAuthUser
    )
    const cognitoUserKeyBase = `${cognitoKeyBase}.${backedUpSession.lastAuthUser}`

    localStorage.setItem(
      `${cognitoUserKeyBase}.accessToken`,
      backedUpSession.accessToken
    )
    localStorage.setItem(
      `${cognitoUserKeyBase}.idToken`,
      backedUpSession.idToken
    )
    localStorage.setItem(
      `${cognitoUserKeyBase}.refreshToken`,
      backedUpSession.refreshToken
    )
    localStorage.setItem(
      `${cognitoUserKeyBase}.deviceKey`,
      backedUpSession.deviceKey
    )

    localStorage.setItem(
      StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY,
      backedUpSession.impersonated
    )
    localStorage.setItem(
      StorageKeys.AUTO_LOGIN_ORIGIN_KEY,
      backedUpSession.origin
    )
    localStorage.setItem(StorageKeys.SALESPAD_KEY, backedUpSession.isSalespad)

    // Clear the backup after restoring
    localStorage.removeItem(StorageKeys.BACKED_UP_SESSION)

    return true
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to restore backed up session:', e)
    return false
  }
}

// Check if there's a backed up session
export const hasBackedUpSession = (): boolean => {
  return !!localStorage.getItem(StorageKeys.BACKED_UP_SESSION)
}
